<template>
  <div class="tw:sm:max-w-lg tw:md:max-w-md tw:mx-auto tw:flex tw:flex-col tw:w-full">
    <div class="tw:flex tw:flex-col tw:flex-1 tw:sm:px-0 tw:pt-16 tw:pb-4 tw:sm:pt-12 tw:md:pb-0">
      <div class="tw:text-center tw:sm:text-left tw:mb-4 tw:sm:mb-6">
        <EdroneHeader
          :current-step="2"
          :desc="translation.subtitle"
          :show-progress="true"
          :title="translation.title"
          :total-steps="4"
        />
      </div>

      <div class="tw:space-y-3">
        <EdroneInput
          :error-msg="formValidationErrors.name"
          :invalid="!!formValidationErrors.name"
          :label="translation.name.label"
          :placeholder="translation.name.placeholder"
          :value="name"
          autocomplete="given-name"
          name="firstName"
          @blur="validateForm('name')"
          @input="(value) => handleChange(value, 'name')"
        />
        <EdroneInput
          :error-msg="formValidationErrorsBackend.email || formValidationErrors.email"
          :invalid="!!formValidationErrorsBackend.email || !!formValidationErrors.email"
          :label="translation.email.label"
          :placeholder="translation.email.placeholder"
          :value="email"
          autocomplete="email"
          name="email"
          @blur="
            () => {
              validateForm('email');
              setShopUrlByEmail();
            }
          "
          @input="(value) => handleChange(value, 'email')"
        />
        <EdroneInput
          :error-msg="formValidationErrors.phoneNumber"
          :invalid="!!formValidationErrors.phoneNumber"
          :label="translation.phone.label"
          :placeholder="translation.phone.placeholder"
          :value="phoneNumber"
          autocomplete="tel"
          name="phoneNumber"
          @blur="validateForm('phoneNumber')"
          @input="(value) => handleChange(value, 'phoneNumber')"
        />
        <EdroneInput
          :error-msg="formValidationErrorsBackend.shopUrl || formValidationErrors.shopUrl"
          :invalid="!!formValidationErrorsBackend.shopUrl || !!formValidationErrors.shopUrl"
          :label="translation.url.label"
          :placeholder="translation.url.placeholder"
          :value="shopUrl"
          autocomplete="url"
          name="shopUrl"
          @blur="handleShopUrlBlur"
          @input="(value) => handleChange(value, 'shopUrl')"
        />
      </div>
      <!-- Desktop buttons - visible only on larger screens -->
      <div class="tw:hidden tw:md:flex tw:gap-2 tw:mt-10">
        <EdroneButtonIcon :disabled="isLoading" unique-id="RegisterForm_goback-button" @click="goBack">
          <font-awesome-icon icon="fa-light fa-arrow-left" />
        </EdroneButtonIcon>
        <EdroneButton
          :disabled="!isFormValid"
          :loading="isLoading"
          class="tw:w-full tw:h-full"
          unique-id="RegisterForm_submit-button"
          @click="handleSubmit"
        >
          {{ translation.submit }}
        </EdroneButton>
      </div>
      <p class="tw:hidden tw:md:block tw:text-center tw:text-xs tw:text-default-600 tw:mt-2">
        *{{ trans._register.platforms.noCardRequired }}
      </p>

      <p
        v-if="formValidationErrorsBackend.captcha"
        class="tw:hidden tw:md:flex tw:md:justify-center tw:caption tw:text-red-500 tw:mt-2"
      >
        {{ translation.notification.error.register }}
      </p>

      <!-- This solution is safe against XSS because translation.agreements.text and links come from a static YAML file in the repository. -->
      <p class="tw:body-2 tw:mt-6" v-html="agreementsText"></p>
    </div>

    <div
      class="tw:fixed tw:bottom-0 tw:left-0 tw:right-0 tw:bg-white tw:border-t tw:border-default-200 tw:p-4 tw:z-20 tw:shadow-lg tw:md:hidden"
    >
      <div class="tw:max-w-xs tw:sm:max-w-xl tw:mx-auto tw:flex tw:flex-col tw:gap-2">
        <EdroneButton
          :disabled="!isFormValid"
          :loading="isLoading"
          class="tw:w-full"
          unique-id="RegisterForm_submit-button-mobile"
          @click="handleSubmit"
        >
          {{ translation.submit }}
        </EdroneButton>
        <p class="tw:caption tw:text-center tw:text-default-600">*{{ trans._register.platforms.noCardRequired }}</p>
      </div>
      <p v-if="formValidationErrorsBackend.captcha" class="tw:caption tw:text-red-500 tw:text-center tw:mt-2">
        {{ translation.notification.error.register }}
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import axios from "axios";
import { ref, onMounted } from "vue";
import { storeToRefs } from "pinia";
import { useRegisterStore } from "vue-app/pages/PageRegister/PageRegister.store";
import { useRouter } from "vue-router";
import { splitAtFirstSpace, validateForm } from "./RegisterForm.helpers";
import EdroneButton from "vue-app/edroneComponents/EdroneButton.vue";
import EdroneInput from "vue-app/edroneComponents/EdroneInput.vue";
import EdroneButtonIcon from "edrone-components/EdroneButtonIcon.vue";
import EdroneHeader from "edrone-components/EdroneHeader.vue";
import { amplitudeSendEvent } from "vue-app/utils/amplitude";
import { useCaptcha } from "vue-app/composables/useCaptcha.ts";

onMounted(() => {
  fetchRestrictedDomains();
  window.scrollTo(0, 0);
});

const translation = window._trans._register.form;

const router = useRouter();

const store = useRegisterStore();
const {
  selectedPlatform,
  isFormValid,
  formValidationErrors,
  formValidationErrorsBackend,
  name,
  phoneNumber,
  email,
  shopUrl,
} = storeToRefs(store);

const isLoading = ref<boolean>(false);
const restrictedDomains = ref<string>("");

const fetchRestrictedDomains = async () => {
  try {
    const { data } = await axios.get(
      "https://dgk28ckagqims.cloudfront.net/mission-control/PageRegister/email_provider_domains.txt",
      {
        headers: {
          "Content-Type": "text/plain",
        },
      }
    );
    restrictedDomains.value = data;
  } catch (e) {
    console.error(e);
  }
};

type FormFieldKey = "name" | "phoneNumber" | "email" | "shopUrl";

const extractStringValue = (value: string | string[]): string => {
  return Array.isArray(value) ? value[0] || "" : value;
};

const updateStoreField = (key: FormFieldKey, value: string): void => {
  switch (key) {
    case "name":
      store.name = value;
      break;
    case "phoneNumber":
      store.phoneNumber = value;
      break;
    case "email":
      store.email = value;
      break;
    case "shopUrl":
      store.shopUrl = value;
      break;
  }
};

const clearBackendValidationError = (key: FormFieldKey): void => {
  if (key === "email") {
    formValidationErrorsBackend.value.email = "";
  }
  if (key === "shopUrl") {
    formValidationErrorsBackend.value.shopUrl = "";
  }
};

const handleChange = (value: string | string[], key: FormFieldKey): void => {
  const stringValue = extractStringValue(value);
  updateStoreField(key, stringValue);
  clearBackendValidationError(key);
};

const setShopUrlByEmail = () => {
  const userDomain = email.value.split("@")[1] ?? "";
  if (userDomain && !shopUrl.value && !restrictedDomains.value.includes(userDomain)) {
    shopUrl.value = `https://${userDomain}`;
  }
};

const handleShopUrlBlur = () => {
  if (shopUrl.value && !shopUrl.value.startsWith("http://") && !shopUrl.value.startsWith("https://")) {
    store.shopUrl = `https://${shopUrl.value}`;
  }
  validateForm("shopUrl");
};

const agreementsText = translation.agreements.text
  .replace(
    /\{Terms of Service\}/g,
    `<a href="${translation.agreements.links.termsOfService}" target="_blank" class="tw:text-blue-500">${translation.agreements.termsOfService}</a>`
  )
  .replace(
    /\{Privacy Policy\}/g,
    `<a href="${translation.agreements.links.privacyPolicy}" target="_blank" class="tw:text-blue-500">${translation.agreements.privacyPolicy}</a>`
  );

const handleSubmit = async () => {
  validateForm();
  if (isFormValid.value) {
    const { executeRecaptcha } = useCaptcha();
    const token = await executeRecaptcha("REGISTER");
    if (token) {
      await register(token);
    }
  } else {
    amplitudeSendEvent("register_form_validation_failed", {
      platform: String(selectedPlatform.value),
      step: "form",
      name_error: formValidationErrors.value.name || "",
      email_error: formValidationErrors.value.email || "",
      phone_error: formValidationErrors.value.phoneNumber || "",
      url_error: formValidationErrors.value.shopUrl || "",
      captcha_error: formValidationErrors.value.captcha || "",
    });
  }
};

const goBack = () => {
  amplitudeSendEvent("register_form_back", {
    platform: String(selectedPlatform.value),
    step: "form",
    name_filled: !!name.value,
    email_filled: !!email.value,
    phone_filled: !!phoneNumber.value,
    url_filled: !!shopUrl.value,
  });
  router.push("/");
};

async function register(token: string) {
  isLoading.value = true;

  amplitudeSendEvent("register_api_request_start", {
    platform: String(selectedPlatform.value),
    step: "form",
  });

  try {
    const { data } = await axios.post("/register", {
      first_name: splitAtFirstSpace(name.value)[0],
      last_name: splitAtFirstSpace(name.value)[1] || "",
      email: email.value,
      subscribe: true,
      phone: phoneNumber.value,
      url: shopUrl.value,
      code: new URL(location.href).searchParams.get("code"),
      onboarding_id: "",
      platform: selectedPlatform.value,
      g_recaptcha_response: token,
      lang: new URL(location.href).searchParams.get("lang"),
      confirmed: new URL(location.href).searchParams.get("confirmed"),
    });

    if (data.status === "success") {
      amplitudeSendEvent("register_success", {
        platform: String(selectedPlatform.value),
        email_domain: email.value.split("@")[1],
        has_referral_code: !!new URL(location.href).searchParams.get("code"),
        language: new URL(location.href).searchParams.get("lang") || "en",
      });
      window.location.href = "/login/trackers";
    } else {
      let errorType = "unknown";
      let errorMessage = "";

      if (data.error._url === "URL already exists") {
        errorType = "url_exists";
        errorMessage = translation.validation.backend.urlExists;
        formValidationErrorsBackend.value.shopUrl = translation.validation.backend.urlExists;
      }
      if (data.error._email === "Email already exists") {
        errorType = "email_exists";
        errorMessage = translation.validation.backend.emailExists;
        formValidationErrorsBackend.value.email = translation.validation.backend.emailExists;
      }

      if (data.error._email === "Email is invalid") {
        errorType = "email_invalid";
        errorMessage = translation.validation.backend.emailIsInvalid;
        formValidationErrorsBackend.value.email = translation.validation.backend.emailIsInvalid;
      }

      if (data.error._captcha === "Captcha not valid") {
        errorType = "captcha_invalid";
        errorMessage = translation.validation.backend.captchaInvalid;
        formValidationErrorsBackend.value.captcha = translation.validation.backend.captchaInvalid;
      }

      amplitudeSendEvent("register_form_api_error", {
        platform: String(selectedPlatform.value),
        error_type: errorType,
        error_message: errorMessage,
        step: "form",
      });
    }
  } catch (error) {
    amplitudeSendEvent("register_form_api_error", {
      platform: String(selectedPlatform.value),
      error_type: "network_error",
      error_message: "Network error or server error",
      step: "form",
    });
  } finally {
    isLoading.value = false;
  }
}
</script>

<style>
@supports (-webkit-touch-callout: none) {
  @media screen and (max-device-width: 812px) and (orientation: portrait) {
    input,
    select,
    textarea {
      font-size: 16px !important;
    }
  }
}
</style>
